import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from 'react-i18next'
import { supportedLanguages } from "@/i18n";

export default function Login() {
  const { t, i18n } = useTranslation()
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [requestedRole, setRequestedRole] = useState("team");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  const needsTurnstile = mode === "login" || mode === "register";

  // Real site key must be injected at build time. No test-key fallback: a missing key is a hard config error.
  const TURNSTILE_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();
  const turnstileMisconfigured = needsTurnstile && !TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!needsTurnstile || !TURNSTILE_SITE_KEY) return;

    const renderTurnstile = () => {
      if (!window.turnstile || !turnstileRef.current || turnstileWidgetId.current !== null) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
        theme: "light",
        size: "flexible",
      });
    };

    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) {
      renderTurnstile();
      existing.addEventListener("load", renderTurnstile);
    } else {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = renderTurnstile;
      document.head.appendChild(script);
    }

    return () => {
      if (window.turnstile && turnstileWidgetId.current !== null) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch {}
      }
      turnstileWidgetId.current = null;
      setTurnstileToken("");
    };
  }, [mode, needsTurnstile, TURNSTILE_SITE_KEY]);

  if (user) return <Navigate to="/" replace />;

  const switchMode = (m) => {
    setMode(m); setError(""); setPassword(""); setConfirm("");
  };

  const resetTurnstile = () => {
    setTurnstileToken("");
    if (window.turnstile && turnstileWidgetId.current !== null) {
      try { window.turnstile.reset(turnstileWidgetId.current); } catch {}
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (mode === "login") {
      if (!turnstileToken) {
        setError(t("completeSecurityCheck", "Please complete the Cloudflare security check."));
        return;
      }
      setLoading(true);
      try { const u = await login(email.trim(), password, turnstileToken); navigate(u?.role === "investor" ? "/portal" : "/"); }
      catch (err) {
        resetTurnstile(); // Turnstile tokens are single-use: a fresh challenge is required for the next attempt
        const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
        if (err.response?.status === 403 && /pending/i.test(detail)) setError(t("perm.accountPending", "Your account is awaiting administrator approval. You will be notified once access is granted."));
        else if (err.response?.status === 403 && /declined/i.test(detail)) setError(t("perm.accountRejected", "Your access request was declined. Please contact the administrator."));
        else setError(detail);
      }
      finally { setLoading(false); }
      return;
    }

    if (mode === "forgot") {
      setLoading(true);
      try {
        await api.post("/auth/forgot-password", { email });
        toast.success(t("resetLinkSent", "If the email exists, a password reset link has been sent."));
        switchMode("login");
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally { setLoading(false); }
      return;
    }

    // register / request access
    if (!name.trim()) { setError(t("onboarding.nameRequired", "Please enter your full name.")); return; }
      if (!phone.trim()) { setError(t("onboarding.phoneRequired", "Please enter your telephone number.")); return; }
    if (password.length < 8) { setError(t("onboarding.passwordTooShort", "Password must be at least 8 characters.")); return; }
    if (password !== confirm) { setError(t("onboarding.passwordMismatch", "Passwords do not match.")); return; }
    if (!turnstileToken) { setError(t("completeSecurityCheck", "Please complete the Cloudflare security check.")); return; }
    setLoading(true);
    try {
      await api.post("/auth/register", {
        name, email, phone: phone.trim(), company, requested_role: requestedRole,
        password, turnstile_token: turnstileToken,
      });
      toast.success(t("onboarding.registerSubmitted", "Your request has been submitted and is pending administrator approval."));
      switchMode("login");
    } catch (err) {
      resetTurnstile();
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const heading = mode === "login" ? t('welcomeBack', 'Welcome back')
    : mode === "register" ? t('onboarding.createAccount', 'Create your account')
    : t('resetPassword', 'Reset password');
  const subheading = mode === "login" ? t('signInToYourConsultancyWorkspace', 'Sign in to your consultancy workspace.')
    : mode === "register" ? t('onboarding.registerSubtitle', 'Request access to the CRM. An administrator will review and approve your account.')
    : t('enterYourEmailToReceiveAResetLink', 'Enter your email to receive a reset link.');

  return (
    <div className="min-h-[100dvh] flex overflow-x-hidden">
      <div className="hidden lg:block lg:w-1/2 relative bg-[#0B2545]">
        <img
          src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt={t('luxuryProperty', 'Luxury property')}
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545] via-[#0B2545]/60 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
            <div className="flex items-center gap-4">
              <img src="/panatlantik-logo.png" alt={t('panatlantikInternational', 'PANATLANTIK INTERNATIONAL')} className="w-20 h-20 object-contain drop-shadow-lg" />
              <div>
                <div className="font-serif text-3xl tracking-tight">{t('panatlantikInternational', 'PANATLANTIK INTERNATIONAL')}</div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mt-2">{t('realEstateCrm', 'Real Estate CRM')}</div>
              </div>
            </div>
          <div>
            <h1 className="font-serif text-4xl xl:text-5xl leading-tight">
              {t('manageYourPremiumPropertyPortfolioWithElegance', 'Manage your premium property portfolio with elegance.')}
            </h1>
            <p className="text-slate-300 mt-4 max-w-md">
              {t('propertiesContactsMatchingPipelineAndAnalyticsInOneProfessionalPlatformForPortugalAndBeyond', 'Properties, contacts, matching, pipeline and analytics — in one professional platform for Portugal and beyond.')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:p-6 bg-[#F8F9FA] min-w-0">
        <div className="w-full max-w-md fade-up min-w-0">
          <div className="flex justify-end mb-4">
            <select
              aria-label={t("language", "Language")}
              value={(i18n.resolvedLanguage || i18n.language || "en").split("-")[0]}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#C5A059]"
            >
              {supportedLanguages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.label}
                </option>
              ))}
            </select>
          </div>
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <img src="/panatlantik-logo.png" alt={t('panatlantikInternational', 'PANATLANTIK INTERNATIONAL')} className="w-12 h-12 sm:w-14 sm:h-14 object-contain shrink-0" />
            <span className="font-serif text-lg sm:text-xl leading-tight text-[#0B2545]">{t('panatlantikInternational', 'PANATLANTIK INTERNATIONAL')}</span>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-slate-900">{heading}</h2>
          <p className="text-slate-500 text-sm sm:text-base mt-2 mb-6 sm:mb-8">{subheading}</p>

          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <Label htmlFor="reg-name">{t('onboarding.fullName', 'Full name')}</Label>
                <Input id="reg-name" data-testid="register-name" value={name}
                  onChange={(e) => setName(e.target.value)} required
                  className="mt-1.5 rounded-sm focus-visible:ring-1 focus-visible:ring-[#C5A059]" />
              </div>
            )}

            <div>
              <Label htmlFor="email">{t('email', 'Email')}</Label>
              <Input id="email" type="email" data-testid="login-email" autoComplete="username"
                inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 rounded-sm h-11 text-base focus-visible:ring-1 focus-visible:ring-[#C5A059]"
                placeholder="you@company.com" />
            </div>

            {mode === "register" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="reg-phone">{t('onboarding.phone', 'Phone')} *</Label>
                    <Input id="reg-phone" type="tel" data-testid="register-phone" value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                        required autoComplete="tel"
                      className="mt-1.5 rounded-sm" placeholder="+351 ..." />
                  </div>
                  <div>
                    <Label htmlFor="reg-company">{t('onboarding.company', 'Company / Agency')}</Label>
                    <Input id="reg-company" data-testid="register-company" value={company}
                      onChange={(e) => setCompany(e.target.value)} className="mt-1.5 rounded-sm" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="reg-role">{t('onboarding.requestedRole', 'Requested role')}</Label>
                  <select id="reg-role" data-testid="register-role" value={requestedRole}
                    onChange={(e) => setRequestedRole(e.target.value)}
                    className="mt-1.5 h-10 w-full border border-slate-200 rounded-sm px-3 bg-white text-sm">
                    <option value="investor">{t('privateClientIndividual', 'Private client / Individual')}</option>
                    <option value="team">{t('onboarding.roleTeam', 'Team member')}</option>
                  </select>
                </div>
              </>
            )}

            {(mode === "login" || mode === "register") && (
              <div>
                <Label htmlFor="password">{mode === "register" ? t('onboarding.createPassword', 'Create password') : t('password', 'Password')}</Label>
                <div className="relative mt-1.5">
                  <Input id="password" type={show ? "text" : "password"} data-testid="login-password"
                    autoComplete={mode === "register" ? "new-password" : "current-password"} value={password}
                    autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    onChange={(e) => setPassword(e.target.value)} required
                    className="rounded-sm h-11 text-base pr-12 focus-visible:ring-1 focus-visible:ring-[#C5A059]"
                    placeholder="••••••••" />
                  <button type="button" onClick={() => setShow(!show)} data-testid="toggle-password-visibility"
                    className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center text-slate-400 hover:text-[#0B2545]"
                    aria-pressed={show} aria-label={show ? t('hidePassword', 'Hide password') : t('showPassword', 'Show password')}>
                    {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            {mode === "register" && (
              <div>
                <Label htmlFor="reg-confirm">{t('onboarding.confirmPassword', 'Confirm password')}</Label>
                <Input id="reg-confirm" type={show ? "text" : "password"} data-testid="register-confirm"
                  autoComplete="new-password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required
                  className="mt-1.5 rounded-sm focus-visible:ring-1 focus-visible:ring-[#C5A059]"
                  placeholder="••••••••" />
              </div>
            )}

            {needsTurnstile && turnstileMisconfigured && (
              <div data-testid="turnstile-misconfigured" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm break-words">
                {t('turnstileNotConfigured', 'Security check is not configured for this deployment (missing REACT_APP_TURNSTILE_SITE_KEY at build time). Please contact the site owner.')}
              </div>
            )}
            {needsTurnstile && !turnstileMisconfigured && (
              <div className="flex justify-center w-full max-w-full overflow-hidden">
                <div ref={turnstileRef} data-testid="turnstile-container" className="w-full max-w-full" />
              </div>
            )}

            {error && (
              <div data-testid="login-error" role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-sm break-words">
                {error}
              </div>
            )}

            <Button type="submit" data-testid="login-submit" disabled={loading || turnstileMisconfigured}
              className="w-full rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white h-11 transition-colors">
              {loading ? t('pleaseWait', 'Please wait…')
                : mode === "login" ? t('signIn', 'Sign in')
                : mode === "register" ? t('onboarding.submitRequest', 'Submit request')
                : t('sendResetLink', 'Send reset link')}
            </Button>

            {mode === "login" && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                <button type="button" onClick={() => switchMode("forgot")}
                  className="text-[#C5A059] hover:underline text-left py-1" data-testid="toggle-forgot">
                  {t('forgotYourPassword', 'Forgot your password?')}
                </button>
                <button type="button" onClick={() => switchMode("register")}
                  className="text-[#C5A059] hover:underline font-medium text-left sm:text-right py-1" data-testid="toggle-register">
                  {t('onboarding.register', 'Register / Request Access')}
                </button>
              </div>
            )}

            {mode !== "login" && (
              <button type="button" onClick={() => switchMode("login")}
                className="text-sm text-[#C5A059] hover:underline w-full text-center" data-testid="toggle-login">
                {t('backToSignIn', 'Back to sign in')}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
